import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import Connection from "../types/connection";
import Option from "../types/option";
import ConnectionType from "../types/connectionType";
import SystemType from "../types/systemType";

interface ConnectionState {
  connection?: Connection;
  connectionList?: Connection[];
  connectionTypes?: ConnectionType[];
  systemTypes?: SystemType[];
  authenticationTypes?: Option[];
  connectionValid?: boolean;
  status: "idle" | "loading" | "failed";
  formStatus: "idle" | "loading" | "failed";
  testStatus: "idle" | "loading" | "failed";
}

const initialState: ConnectionState = {
  status: "idle",
  formStatus: "idle",
  testStatus: "idle",
};

export const createConnection = createAsyncThunk<Connection, Connection>(
  "connection/add",
  async (vm: Connection) => {
    const response = await http.post<Connection>(`/connection`, vm, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const fetchConnections = createAsyncThunk<Connection[]>(
  "connections/fetch",
  async () => {
    const response = await http.get<Connection[]>("/connection");
    return response.data;
  }
);

export const fetchConnection = createAsyncThunk<Connection, number>(
  "connection/fetch",
  async (id: number) => {
    const response = await http.get<Connection>(`/connection/${id}`);
    return response.data;
  }
);

export const editConnection = createAsyncThunk<Connection, Connection>(
  "connection/edit",
  async (vm: Connection) => {
    const response = await http.put<Connection>(`/connection/${vm.id}`, vm, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const deleteConnection = createAsyncThunk<null, number>(
  "connections/delete",
  async (id: number) => {
    const response = await http.delete(`/connection/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const fetchConnectionTypes = createAsyncThunk<ConnectionType[]>(
  "connection/fetch-types",
  async () => {
    const response = await http.get<ConnectionType[]>(`/connection/types`);
    return response.data;
  }
);

export const testConnection = createAsyncThunk<boolean, Connection>(
  "connection/test",
  async (vm: Connection) => {
    const response = await http.post<boolean>(`/connection/test`, vm);
    return response.data;
  }
);

export const fetchSystemTypes = createAsyncThunk<SystemType[]>(
  "connection/fetch-system-types",
  async () => {
    const response = await http.get<SystemType[]>(`/connection/systems`);
    return response.data;
  }
);

export const fetchAuthTypes = createAsyncThunk<Option[], number>(
  "connection/fetch-auth-types",
  async (systemTypeId: number) => {
    const response = await http.get<Option[]>(
      `/connection/authenticationTypes/${systemTypeId}`
    );
    return response.data;
  }
);

const connectionSlice = createSlice({
  name: "connection",
  initialState,
  reducers: {
    clearConnection(state) {
      state.connection = undefined;
      state.connectionValid = undefined;
    },
    clearConnectionOptions(state) {
      state.connectionList = initialState.connectionList;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConnections.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchConnections.fulfilled, (state, action) => {
        state.status = "idle";
        state.connectionList = action.payload;
      })
      .addCase(fetchConnections.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(fetchConnection.pending, (state) => {
        state.status = "loading";
        state.formStatus = "loading";
      })
      .addCase(fetchConnection.fulfilled, (state, action) => {
        state.status = "idle";
        state.formStatus = "idle";
        state.connection = action.payload;
      })
      .addCase(fetchConnection.rejected, (state, action) => {
        state.status = "failed";
        state.formStatus = "failed";
      })
      .addCase(testConnection.pending, (state) => {
        state.testStatus = "loading";
      })
      .addCase(testConnection.fulfilled, (state, action) => {
        state.testStatus = "idle";
        state.connectionValid = action.payload;
      })
      .addCase(testConnection.rejected, (state, action) => {
        state.testStatus = "failed";
      })
      .addCase(createConnection.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createConnection.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(createConnection.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(editConnection.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editConnection.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(editConnection.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(fetchConnectionTypes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchConnectionTypes.fulfilled, (state, action) => {
        state.status = "idle";
        state.connectionTypes = action.payload;
      })
      .addCase(fetchConnectionTypes.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(fetchSystemTypes.pending, (state) => {
        state.status = "loading";
        state.formStatus = "loading";
      })
      .addCase(fetchSystemTypes.fulfilled, (state, action) => {
        state.status = "idle";
        state.formStatus = "idle";
        state.systemTypes = action.payload;
      })
      .addCase(fetchSystemTypes.rejected, (state, action) => {
        state.status = "failed";
        state.formStatus = "failed";
      })
      .addCase(fetchAuthTypes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAuthTypes.fulfilled, (state, action) => {
        state.status = "idle";
        state.authenticationTypes = action.payload;
      })
      .addCase(fetchAuthTypes.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(deleteConnection.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteConnection.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(deleteConnection.rejected, (state, action) => {
        state.status = "failed";
      });
  },
});

export const { clearConnection, clearConnectionOptions } =
  connectionSlice.actions;

export default connectionSlice.reducer;
